import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useEffect} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {AnswerFolderModel} from '../../../../models/api/answerFolderModel';
import {AnswerFolderDropdownContainer} from './answerFolderDropdownContainer';
import {AnswerFolderDropdownInfoTooltip} from './answerFolderDropdownInfoTooltip';

/*
 * Props.
 */

export interface AnswerFolderDropdownBoxProps {
  onFolderSelect: HandlerOf<number | undefined>;
  selectedFolderId: number | undefined;
  selectedFolder: AnswerFolderModel | undefined;
  allAvailableNamespaces: ReadonlyArray<string>;
  selectedNamespace?: string;
  onRequestClose?: Handler;
  shouldHideLabel?: boolean;
  shouldHideTooltipIcon?: boolean;
  dropdownWidth?: number;
  currentFolder?: AnswerFolderModel;
}

/*
 * Style.
 */

const StyledNameDiv = styled.div`
  ${ellipsis()};
`;

const StyledLabelDiv = styled.div`
  display: flex;
`;

const StyledInfoIconWrapperDiv = styled.div`
  display: inline-block;
  margin-bottom: -4px;
  margin-left: 5px;
`;

/*
 * Component.
 */

export const AnswerFolderDropdownBox: FC<AnswerFolderDropdownBoxProps> = (props) => {
  const {
    onFolderSelect,
    selectedFolder,
    selectedNamespace,
    allAvailableNamespaces,
    onRequestClose,
    shouldHideLabel,
    dropdownWidth,
    currentFolder,
    shouldHideTooltipIcon,
  } = props;

  // If the user changed the Access namespace, reset the folder if it isn't in that namespace.
  useEffect(() => {
    if (!selectedNamespace) {
      return;
    }

    if (selectedNamespace !== selectedFolder?.namespace) {
      onFolderSelect(undefined);
    }
  }, [selectedNamespace, selectedFolder, onFolderSelect]);

  const renderDropdownCallback = useCallback(
    (popoverProps: PopoverTransferProps) =>
      renderDropdown(
        popoverProps,
        onFolderSelect,
        selectedFolder,
        selectedNamespace ? [selectedNamespace] : allAvailableNamespaces,
        dropdownWidth,
        currentFolder,
      ),
    [onFolderSelect, selectedFolder, selectedNamespace, allAvailableNamespaces, dropdownWidth, currentFolder],
  );

  return (
    <DropdownBoxCoordinator
      label={maybeRenderAnswerFolderDropdownLabel(shouldHideLabel, shouldHideTooltipIcon, onRequestClose)}
      value={renderValue(selectedFolder)}
      renderDropdown={renderDropdownCallback}
      shouldDisablePadding
    />
  );
};

function maybeRenderAnswerFolderDropdownLabel(
  shouldHideLabel?: boolean,
  shouldHideTooltipIcon?: boolean,
  onRequestClose?: Handler,
) {
  if (shouldHideLabel) {
    return null;
  }

  return (
    <StyledLabelDiv>
      <Trans id="answer-folder-dropdown-label">File folder under</Trans>
      {maybeRenderInfoTipIcon(shouldHideTooltipIcon, onRequestClose)}
    </StyledLabelDiv>
  );
}

function maybeRenderInfoTipIcon(shouldHideTooltipIcon?: boolean, onRequestClose?: Handler) {
  if (shouldHideTooltipIcon) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledInfoIconWrapperDiv>
          <TooltipCoordinator
            persistent
            iconColor={theme.greys.shade80}
            placement="top"
            render={() => <AnswerFolderDropdownInfoTooltip onRequestClose={onRequestClose} />}
            isMultiline
          />
        </StyledInfoIconWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderValue(selectedFolder?: AnswerFolderModel) {
  return (
    <StyledNameDiv>
      {selectedFolder ? selectedFolder.name : <Trans id="answer-folder-dropdown-box-none">None</Trans>}
    </StyledNameDiv>
  );
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  onFolderSelect: HandlerOf<number | undefined>,
  selectedFolder: AnswerFolderModel | undefined,
  namespaces: ReadonlyArray<string>,
  dropdownWidth: number | undefined,
  currentFolder: AnswerFolderModel | undefined,
) {
  return (
    <AnswerFolderDropdownContainer
      {...popoverProps} /* eslint-disable-line react/jsx-props-no-spreading */
      width={dropdownWidth || popoverProps.anchorWidth}
      onFolderSelect={onFolderSelect}
      selectedFolder={selectedFolder}
      namespaces={namespaces}
      currentFolder={currentFolder}
    />
  );
}
